import { useState, type ReactElement } from 'react';
import { Box, Text } from '@mantine/core';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PolicyOutputChip from './PolicyOutputChip';
import PolicyTurnDrawer, { type SelectedTurn } from './PolicyTurnDrawer';
import { formatPct, formatWhen } from './PolicyFormatting';
import type { ClassifiedItem } from './PolicyVerdict';
import type { PolicyView } from './PolicyTypes';

const COLUMNS: MegaGridColumn<ClassifiedItem>[] = [
    {
        key: 'when',
        header: 'When',
        sortValue: (i) => new Date(i.timestampIso),
        render: (i) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatWhen(i.timestampIso)}
            </Text>
        ),
    },
    {
        key: 'person',
        header: 'Person',
        sortValue: (i) => i.person,
        groupValue: (i) => i.department,
        render: (i) => (
            <Box>
                <Text size="xs" fw={600}>
                    {i.person}
                </Text>
                <Text c="dimmed" style={{ fontSize: '9px' }}>
                    {i.department}
                </Text>
            </Box>
        ),
    },
    {
        key: 'conf',
        header: 'Conf.',
        align: 'right',
        sortValue: (i) => i.confidence,
        render: (i) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatPct(i.confidence)}
            </Text>
        ),
    },
    {
        key: 'verdict',
        header: 'Verdict',
        align: 'right',
        sortValue: (i) => i.output,
        filter: { type: 'enum', accessor: (i) => i.output },
        render: (i) => <PolicyOutputChip output={i.output} tone={i.tone} />,
    },
];

/**
 * Dense, drill-down table of the turns classified under a single policy, mirroring
 * the surface evidence table. Clicking a row opens the turn's conversation and verdict.
 */
export default function PolicyEvidenceTable({ view, items, persistenceKey }: { view: PolicyView; items: ClassifiedItem[]; persistenceKey?: string }): ReactElement {
    const [selected, setSelected] = useState<ClassifiedItem | null>(null);

    return (
        <>
            <MegaGrid
                columns={COLUMNS}
                rows={items}
                getRowKey={(i) => i.id}
                onRowClick={(i) => setSelected(i)}
                persistenceKey={persistenceKey}
                searchAccessor={(i) => `${i.person} ${i.department} ${i.output}`}
                searchPlaceholder="Search prompts"
                striped
                density="compact"
                minWidth={480}
                maxHeight="60vh"
            />

            <PolicyTurnDrawer
                selected={selected === null ? null : ({ item: selected, policyName: view.policy.name } satisfies SelectedTurn)}
                onClose={() => setSelected(null)}
            />
        </>
    );
}
